/**
 * Normalize Livepass-style cart API payloads and merge into session checkout shape.
 */

import { coerceCartEventKey, eventKeysEqual } from './event-route-key.util';
import { resolveCanonicalEventSlug } from './event-navigation.util';

export function extractCartRecord(root: unknown): Record<string, unknown> | null {
  if (root == null || typeof root !== 'object') return null;
  const r = root as Record<string, unknown>;
  const d = r['data'];
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const inner = d as Record<string, unknown>;
    if (
      inner['subtotal'] != null ||
      inner['sub_total'] != null ||
      inner['total'] != null ||
      inner['total_amount'] != null ||
      inner['grand_total'] != null ||
      Array.isArray(inner['items']) ||
      Array.isArray(inner['ticket_types'])
    ) {
      return inner;
    }
    const dd = inner['data'];
    if (dd && typeof dd === 'object' && !Array.isArray(dd)) {
      return dd as Record<string, unknown>;
    }
    return inner;
  }
  return r;
}

function pickUnknown(record: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function pickText(record: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  const value = pickUnknown(record, ...keys);
  if (typeof value !== 'string') return '';
  const text = value.trim();
  return text;
}

function pickNum(cart: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = cart[k];
    if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) {
      return Number(v);
    }
  }
  return undefined;
}

/** Rupees with up to 2 decimal places (API returns e.g. 641.8138). */
function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function coerceTicketTypeKeyFromRow(o: Record<string, unknown>): string | number | null {
  const raw = o['ticket_types_id'] ?? o['ticket_type_id'] ?? o['ticket_id'] ?? null;
  return coerceCartEventKey(raw);
}

function mapCartLinesToItems(
  rows: unknown[],
  base: Record<string, unknown>
): Array<{ ticket: Record<string, unknown>; qty: number; cartLineId?: number }> {
  const baseItems = base['items'] as Array<{ ticket: Record<string, unknown>; qty: number }> | undefined;
  const currency = String(base['currency'] ?? 'INR');
  return rows.map((row) => {
    const o = row as Record<string, unknown>;
    const ticketTypeKey = coerceTicketTypeKeyFromRow(o);
    const lineId = Number(o['id'] ?? 0);
    const id: string | number =
      ticketTypeKey != null ? ticketTypeKey : lineId > 0 ? lineId : 0;
    const qty = Math.max(1, Number(o['quantity'] ?? o['qty'] ?? 1));
    const name = String(o['name'] ?? o['ticket_name'] ?? o['title'] ?? '').trim();
    const price = Number(o['price'] ?? o['unit_price'] ?? o['amount'] ?? 0);
    const fromBase = baseItems?.find((b) => eventKeysEqual(b.ticket?.['id'], id));
    const bt = fromBase?.ticket;
    const isGst = o['is_gst'];
    const ticket: Record<string, unknown> =
      bt && typeof bt === 'object'
        ? {
            ...bt,
            id: id || bt['id'],
            name: name || String(bt['name'] ?? ''),
            price: price || Number(bt['price'] ?? 0),
            currency: bt['currency'] ?? currency,
            ...(isGst !== undefined ? { is_gst: isGst } : {})
          }
        : {
            id,
            name: name || 'Ticket',
            price,
            currency,
            ...(isGst !== undefined ? { is_gst: isGst } : {})
          };
    const cartLineId = lineId > 0 ? lineId : undefined;
    return { ticket, qty, ...(cartLineId != null ? { cartLineId } : {}) };
  });
}

/**
 * Merge server cart totals/lines into local checkout (from session). Keeps event/show/eventId.
 */
export function mergeCartApiIntoCheckout(
  base: Record<string, unknown>,
  cartRoot: unknown
): Record<string, unknown> {
  const cart = extractCartRecord(cartRoot);
  const out = { ...base };
  if (!cart) {
    out['cartApiRaw'] = cartRoot;
    return out;
  }

  const sub = pickNum(cart, 'sub_total', 'subtotal', 'ticket_total', 'tickets_total', 'amount_subtotal');
  if (sub != null) out['subtotal'] = roundMoney(sub);

  const total = pickNum(cart, 'total_amount', 'total', 'grand_total', 'amount_total', 'payable', 'final_total');
  if (total != null) out['total'] = roundMoney(total);

  const gst = pickNum(cart, 'gst_amount', 'gst', 'tax_amount', 'total_gst');
  if (gst != null) out['gstAmount'] = roundMoney(gst);

  const convTotal = pickNum(
    cart,
    'total_convenience_fee',
    'convenience_fee_amount',
    'convenience_fee',
    'convenienceFee',
    'platform_fee',
    'convenience'
  );
  if (convTotal != null) {
    const c = roundMoney(convTotal);
    out['total_convenience_fee'] = c;
    out['convenience_fee'] = c;
    out['convenienceFee'] = c;
  }

  const convBase = pickNum(cart, 'convenience_fee_amount', 'convenience_fee', 'convenienceFee');
  if (convBase != null) {
    out['convenience_fee_amount'] = roundMoney(convBase);
  }

  const convPct = pickNum(cart, 'convenience_fee_percentage');
  if (convPct != null) out['convenience_fee_percentage'] = convPct;

  const cgst = pickNum(cart, 'cgst');
  const sgst = pickNum(cart, 'sgst');
  const igst = pickNum(cart, 'igst');
  if (cgst != null) out['cart_cgst'] = roundMoney(cgst);
  if (sgst != null) out['cart_sgst'] = roundMoney(sgst);
  if (igst != null) out['cart_igst'] = roundMoney(igst);

  const fee = pickNum(cart, 'fee', 'fees_total', 'total_fees');
  if (fee != null) out['fee'] = roundMoney(fee);
  else {
    const g = Number(out['gstAmount'] ?? 0);
    const cf = Number(out['convenience_fee'] ?? out['convenienceFee'] ?? 0);
    if (g || cf) out['fee'] = roundMoney(g + cf);
  }

  const itemsRaw = cart['items'] ?? cart['ticket_types'] ?? cart['lines'] ?? cart['cart_items'];
  if (Array.isArray(itemsRaw)) {
    try {
      out['items'] = mapCartLinesToItems(itemsRaw, out);
      const tierParts = (out['items'] as Array<{ ticket: { name?: string }; qty: number }>).map(
        (it) => `${it.ticket?.name ?? 'Ticket'} × ${it.qty}`
      );
      out['tierName'] = tierParts.join(', ');
      out['qty'] = (out['items'] as Array<{ qty: number }>).reduce((s, it) => s + it.qty, 0);

      const anyItemGst = itemsRaw.some((row) => {
        const r = row as Record<string, unknown>;
        return r['is_gst'] === true || r['is_gst'] === 1 || r['is_gst'] === '1';
      });
      if (anyItemGst) {
        out['is_gst'] = 1;
        out['gst'] = 1;
      }
    } catch {
      /* keep base items */
    }
  }

  const ig = cart['is_gst'] ?? cart['gst_enabled'];
  if (ig !== undefined && ig !== null && out['is_gst'] === undefined) {
    out['is_gst'] = ig === 1 || ig === true || ig === '1' ? 1 : 0;
    out['gst'] = out['is_gst'];
  }

  out['cartApi'] = cart;
  out['cartApiRaw'] = cartRoot;

  // Preserve seated-booking fields from session checkout (GET /cart may not echo them).
  for (const key of [
    'seated_booking',
    'selectedSeats',
    'seat_labels',
    'seat_codes',
    'seat_ids',
    'ids',
    'hold_token',
    'holdSeatsResponse',
    'organizer',
    'seatsio_hold_token',
    'seatsioHoldToken',
    'seatsioEventKey',
    'seatsio_event_key',
    'seatsio_chart_key',
    'selectedSeatObjects'
  ] as const) {
    if (base[key] != null && base[key] !== '' && (out[key] == null || out[key] === '')) {
      out[key] = base[key];
    }
  }

  return out;
}

export function buildCheckoutFromCart(
  cartRoot: unknown,
  existingBase?: Record<string, unknown> | null
): Record<string, unknown> | null {
  const cart = extractCartRecord(cartRoot);
  if (!cart) return null;

  const itemsRaw = cart['items'] ?? cart['ticket_types'] ?? cart['lines'] ?? cart['cart_items'];
  if (!Array.isArray(itemsRaw) || itemsRaw.length === 0) return null;

  const primary = itemsRaw[0] as Record<string, unknown>;
  const base = existingBase ?? {};
  const existingEvent =
    base['event'] && typeof base['event'] === 'object' && !Array.isArray(base['event'])
      ? (base['event'] as Record<string, unknown>)
      : null;
  const existingShow =
    base['show'] && typeof base['show'] === 'object' && !Array.isArray(base['show'])
      ? (base['show'] as Record<string, unknown>)
      : null;

  const eventId = coerceCartEventKey(
    pickUnknown(cart, 'event_id', 'eventId') ??
      pickUnknown(primary, 'event_id', 'eventId') ??
      pickUnknown(base, 'eventId', 'event_id') ??
      pickUnknown(existingEvent, 'id')
  );
  if (eventId == null) return null;

  const showId = coerceCartEventKey(
    pickUnknown(cart, 'show_id', 'showId') ??
      pickUnknown(primary, 'show_id', 'showId') ??
      pickUnknown(base, 'showId') ??
      pickUnknown(existingShow, 'id', 'show_id')
  );

  const items = mapCartLinesToItems(itemsRaw, base);
  const currency = pickText(cart, 'currency') || pickText(primary, 'currency') || String(base['currency'] ?? 'INR');
  const eventTitle =
    pickText(existingEvent, 'title', 'name', 'event_name', 'event_title') ||
    pickText(primary, 'event_title', 'event_name', 'title', 'name') ||
    `Event #${String(eventId)}`;
  const eventVenue =
    pickText(existingEvent, 'venue', 'venue_name', 'address', 'city') ||
    pickText(primary, 'venue', 'venue_name', 'address', 'city', 'location') ||
    '';
  const eventImage =
    pickText(existingEvent, 'image_url', 'event_image', 'event_image_url', 'thumbnail_image') ||
    pickText(primary, 'image_url', 'event_image', 'event_image_url', 'thumbnail_image', 'image') ||
    '';
  const eventPayload: Record<string, unknown> = {
    ...(existingEvent ?? {}),
    id: eventId,
    slug: resolveCanonicalEventSlug(
      {
        ...(existingEvent ?? {}),
        ...(cart as Record<string, unknown>),
        ...(primary as Record<string, unknown>),
        title: eventTitle
      },
      eventId
    ),
    title: eventTitle,
    venue: eventVenue,
    ...(eventImage ? { image_url: eventImage } : {})
  };

  const showDate =
    pickText(existingShow, 'show_date') ||
    pickText(primary, 'show_date', 'date') ||
    pickText(cart, 'show_date', 'date') ||
    pickText(base, 'date');
  const startTime =
    pickText(existingShow, 'start_time', 'startTime') ||
    pickText(primary, 'start_time', 'startTime', 'time') ||
    pickText(cart, 'start_time', 'startTime', 'time') ||
    pickText(base, 'time');
  const showPayload: Record<string, unknown> = {
    ...(existingShow ?? {}),
    ...(showId != null ? { id: showId, show_id: showId } : {}),
    ...(showDate ? { show_date: showDate } : {}),
    ...(startTime ? { start_time: startTime } : {})
  };

  const seed: Record<string, unknown> = {
    ...base,
    eventId,
    ...(showId != null ? { showId } : {}),
    currency,
    items,
    qty: items.reduce((sum, item) => sum + Number(item.qty ?? 0), 0),
    tierName: items.map((item) => `${String(item.ticket?.['name'] ?? 'Ticket')} x ${Number(item.qty ?? 0)}`).join(', '),
    ticketId: items[0]?.ticket?.['id'] ?? base['ticketId'],
    ...(showDate ? { date: showDate } : {}),
    ...(startTime ? { time: startTime } : {}),
    event: eventPayload,
    show: showPayload,
    review_session_id:
      pickText(base, 'review_session_id') ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  };

  return mergeCartApiIntoCheckout(seed, cartRoot);
}
